// PublicVacancyPage.tsx - Versión mejorada

import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Chip,
  Pagination,
  Divider,
  InputAdornment,
  RadioGroup,
  FormControlLabel,
  Radio,
  Slider,
  Paper,
  IconButton,
  Collapse,
  Alert
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BusinessIcon from '@mui/icons-material/Business';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import WorkIcon from '@mui/icons-material/Work';
import SchoolIcon from '@mui/icons-material/School';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { getPublicVacancies, getVacancyFilters } from '../../modules/apps/user-management/users-list/core/_requests';

// Tipos de búsqueda
type SearchType = 'general' | 'title' | 'company' | 'keyword';

const PublicVacancyPage: React.FC = () => {
  const navigate = useNavigate();
  const [vacancies, setVacancies] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [filters, setFilters] = useState<any>({});
  const [filterOptions, setFilterOptions] = useState<any>({});
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [searchType, setSearchType] = useState<SearchType>('general');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 9,
    total: 0,
    totalPages: 0
  });
  const [showFilters, setShowFilters] = useState<boolean>(false);

  // Estado para el filtro de salario flexible
  const [salaryMin, setSalaryMin] = useState<number | ''>('');
  const [salaryMax, setSalaryMax] = useState<number | ''>('');
  const [salaryRange, setSalaryRange] = useState<number[]>([0, 10000000]);

  useEffect(() => {
    loadFilters();
  }, []);

  useEffect(() => {
    loadVacancies();
  }, [pagination.page, filters, searchType]);

  const loadFilters = async () => {
    try {
      const response = await getVacancyFilters();
      if (response.success) {
        setFilterOptions(response.data);
        // Establecer rango máximo de salario basado en datos disponibles
        if (response.data.salaryStats?.maxSalary) {
          setSalaryRange([0, response.data.salaryStats.maxSalary]);
        }
      }
    } catch (error) {
      console.error('Error loading filters:', error);
    }
  };

  const loadVacancies = async () => {
    setLoading(true);
    try {
      const params: any = {
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      };

      // Agregar búsqueda según el tipo seleccionado
      if (searchTerm.trim()) {
        params.search = searchTerm;

        switch (searchType) {
          case 'title':
            params.search_by_title = true;
            break;
          case 'company':
            params.search_by_company = true;
            break;
          case 'keyword':
            params.search_by_keyword = true;
            break;
          default:
            // Búsqueda general - no se añaden parámetros específicos
            break;
        }
      }

      // Agregar filtros de salario flexible
      if (salaryMin !== '' && salaryMin > 0) {
        params.salario_min = salaryMin;
      }
      if (salaryMax !== '' && salaryMax > 0) {
        params.salario_max = salaryMax;
      }

      const response = await getPublicVacancies(params);
      if (response.success) {
        setVacancies(response.data);
        setPagination(prev => ({
          ...prev,
          total: response.pagination.total,
          totalPages: response.pagination.totalPages
        }));
      }
    } catch (error) {
      console.error('Error loading vacancies:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    loadVacancies();
  };

  const handleFilterChange = (key: string, value: any) => {
    setFilters((prev: any) => ({
      ...prev,
      [key]: value
    }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleSalaryRangeApply = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    loadVacancies();
  };

  const handleSalaryMinChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value === '' ? '' : Number(event.target.value);
    setSalaryMin(value);
  };

  const handleSalaryMaxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value === '' ? '' : Number(event.target.value);
    setSalaryMax(value);
  };

  const clearSalaryFilters = () => {
    setSalaryMin('');
    setSalaryMax('');
    setSalaryRange([0, 10000000]);
    setPagination(prev => ({ ...prev, page: 1 }));
    setTimeout(() => loadVacancies(), 100);
  };

  const clearFilters = () => {
    setFilters({});
    setSearchTerm('');
    setSearchType('general');
    setSalaryMin('');
    setSalaryMax('');
    setSalaryRange([0, 10000000]);
    setPagination(prev => ({ ...prev, page: 1 }));
    setTimeout(() => loadVacancies(), 100);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPagination(prev => ({ ...prev, page: value }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Formatear salario para mostrar
  const formatSalaryForDisplay = (value: number) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(value);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h3" gutterBottom color="primary">
          Busco Empleo - Portal de Vacantes
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Encuentra las mejores oportunidades laborales en Bogotá
        </Typography>
      </Box>

      {/* Barra de búsqueda con tipos */}
      <Paper elevation={3} sx={{ mb: 4, p: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={7}
            sx={{
              '& input::placeholder': {
                whiteSpace: 'normal'
              }
            }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder={
                searchType === 'title'
                  ? 'Buscar por nombre de la vacante...'
                  : searchType === 'company'
                    ? 'Buscar por nombre de la empresa...'
                    : searchType === 'keyword'
                      ? 'Buscar por palabras clave en la descripción...'
                      : 'Buscar por nombre de vacante, empresa o palabras clave...'
              }
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Tipo de búsqueda</InputLabel>
              <Select
                value={searchType}
                label="Tipo de búsqueda"
                onChange={(e) => setSearchType(e.target.value as SearchType)}
              >
                <MenuItem value="general">Búsqueda general</MenuItem>
                <MenuItem value="title">Por nombre de vacante</MenuItem>
                <MenuItem value="company">Por nombre de empresa</MenuItem>
                <MenuItem value="keyword">Por palabras clave</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={1}>
            <Button
              fullWidth
              variant={showFilters ? "contained" : "outlined"}
              startIcon={<FilterListIcon />}
              onClick={() => setShowFilters(!showFilters)}
            >
              Filtros
            </Button>
          </Grid>
          <Grid item xs={6} md={1}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={handleSearch}
            >
              Buscar
            </Button>
          </Grid>
        </Grid>

        {/* Indicador del tipo de búsqueda activo */}
        {searchTerm && (
          <Box sx={{ mt: 2 }}>
            <Chip
              label={`Buscando: ${searchTerm} (${searchType === 'title' ? 'por título' : searchType === 'company' ? 'por empresa' : searchType === 'keyword' ? 'por palabras clave' : 'búsqueda general'})`}
              onDelete={() => {
                setSearchTerm('');
                handleSearch();
              }}
              color="primary"
              variant="outlined"
            />
          </Box>
        )}
      </Paper>

      {/* Filtros desplegables mejorados */}
      <Collapse in={showFilters}>
        <Card sx={{ mb: 4, p: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Filtrar por:
            </Typography>
            <IconButton onClick={() => setShowFilters(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo de contrato</InputLabel>
                <Select
                  value={filters.tipo_contrato || ''}
                  label="Tipo de contrato"
                  onChange={(e) => handleFilterChange('tipo_contrato', e.target.value)}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {filterOptions.tiposContrato?.map((option: any) => (
                    <MenuItem key={option.code} value={option.code}>
                      {option.value}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Nivel de estudio</InputLabel>
                <Select
                  value={filters.nivel_estudio || ''}
                  label="Nivel de estudio"
                  onChange={(e) => handleFilterChange('nivel_estudio', e.target.value)}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {filterOptions.nivelesEstudio?.map((option: any) => (
                    <MenuItem key={option.code} value={option.code}>
                      {option.value}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Modalidad</InputLabel>
                <Select
                  value={filters.modalidad || ''}
                  label="Modalidad"
                  onChange={(e) => handleFilterChange('modalidad', e.target.value)}
                >
                  <MenuItem value="">Todas</MenuItem>
                  {filterOptions.modalidades?.map((option: any) => (
                    <MenuItem key={option.code} value={option.code}>
                      {option.value}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Jornada laboral</InputLabel>
                <Select
                  value={filters.jornada_laboral || ''}
                  label="Jornada laboral"
                  onChange={(e) => handleFilterChange('jornada_laboral', e.target.value)}
                >
                  <MenuItem value="">Todas</MenuItem>
                  {filterOptions.jornadasLaborales?.map((option: any) => (
                    <MenuItem key={option.code} value={option.code}>
                      {option.value}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Filtro de rango salarial flexible */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                  Rango salarial flexible
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  Define el límite inferior y/o superior según tu preferencia
                </Typography>

                <Grid container spacing={2} alignItems="flex-end">
                  <Grid item xs={12} sm={5}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Salario mínimo (desde)"
                      value={salaryMin}
                      onChange={handleSalaryMinChange}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                      placeholder="Ej: 2000000"
                    />
                  </Grid>
                  <Grid item xs={12} sm={5}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Salario máximo (hasta)"
                      value={salaryMax}
                      onChange={handleSalaryMaxChange}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                      placeholder="Ej: 5000000"
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleSalaryRangeApply}
                      disabled={salaryMin === '' && salaryMax === ''}
                    >
                      Aplicar
                    </Button>
                  </Grid>
                </Grid>

                {/* Mostrar rango seleccionado */}
                {(salaryMin !== '' || salaryMax !== '') && (
                  <Box sx={{ mt: 2 }}>
                    <Alert
                      severity="info"
                      action={
                        <Button color="inherit" size="small" onClick={clearSalaryFilters}>
                          Limpiar
                        </Button>
                      }
                    >
                      <Typography variant="body2">
                        Filtrando salario:
                        {salaryMin !== '' && ` desde ${formatSalaryForDisplay(Number(salaryMin))}`}
                        {salaryMax !== '' && ` hasta ${formatSalaryForDisplay(Number(salaryMax))}`}
                      </Typography>
                    </Alert>
                  </Box>
                )}

                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  * Puedes definir solo el límite inferior, solo el superior, o ambos
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button onClick={clearFilters} color="secondary" variant="outlined">
                  Limpiar todos los filtros
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Card>
      </Collapse>

      {/* Resultados */}
      {loading ? (
        <Box textAlign="center" py={4}>
          <Typography>Cargando vacantes...</Typography>
        </Box>
      ) : vacancies.length === 0 ? (
        <Card sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No se encontraron vacantes con los criterios de búsqueda
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Intenta con diferentes palabras clave o amplía el rango de búsqueda
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={clearFilters}
            sx={{ mt: 2 }}
          >
            Limpiar filtros y buscar de nuevo
          </Button>
        </Card>
      ) : (
        <>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              {pagination.total} vacantes encontradas
            </Typography>
            <Chip
              label={`Mostrando ${vacancies.length} de ${pagination.total}`}
              color="primary"
              variant="outlined"
            />
          </Box>

          <Grid container spacing={3}>
            {vacancies.map((vacancy: any) => (
              <Grid item xs={12} md={6} lg={4} key={vacancy.id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-4px)', boxShadow: 6 } }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    {/* Indicador de confidencialidad */}
                    {vacancy.es_confidencial && (
                      <Chip
                        label="Confidencial"
                        size="small"
                        color="warning"
                        sx={{ mb: 1 }}
                      />
                    )}

                    {/* Código de vacante */}
                    <Chip
                      label={`Código: ${vacancy.codigo_vacante || 'Sin código'}`}
                      size="small"
                      color="primary"
                      sx={{ mb: 2 }}
                    />

                    {/* Título */}
                    <Typography variant="h6" gutterBottom fontWeight="bold">
                      {vacancy.nombre_vacante}
                    </Typography>

                    {/* Empresa - Manejar confidencialidad */}
                    <Box display="flex" alignItems="center" mb={1}>
                      <BusinessIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        {vacancy.es_confidencial
                          ? 'Empresa confidencial'
                          : vacancy.empresa?.nombre || 'Empresa no especificada'}
                      </Typography>
                    </Box>

                    {/* Descripción resumida */}
                    <Typography variant="body2" color="text.secondary"
                      sx={{
                        mb: 2,
                        overflow: 'visible',
                        display: 'block',
                        whiteSpace: 'normal'
                      }}>
                      {vacancy.descripcion}
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    {/* Detalles */}
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <Box display="flex" alignItems="center">
                          <MonetizationOnIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                          <Typography variant="body2">
                            <strong>Salario:</strong> {vacancy.salario?.minimo_formateado || 'A convenir'}
                            {vacancy.salario?.maximo && ` - ${vacancy.salario.maximo_formateado}`}
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid item xs={12}>
                        <Box display="flex" alignItems="center">
                          <WorkIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                          <Typography variant="body2">
                            <strong>Contrato:</strong> {vacancy.tipo_contrato?.label || 'No especificado'}
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid item xs={12}>
                        <Box display="flex" alignItems="center">
                          <SchoolIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                          <Typography variant="body2">
                            <strong>Estudio:</strong> {vacancy.nivel_educativo?.label || 'No especificado'}
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid item xs={12}>
                        <Box display="flex" alignItems="center">
                          <AccessTimeIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                          <Typography variant="body2">
                            <strong>Experiencia:</strong> {vacancy.experiencia?.label || 'No especificada'}
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid item xs={12}>
                        <Box display="flex" alignItems="center">
                          <LocationOnIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                          <Typography variant="body2">
                            <strong>Modalidad:</strong> {vacancy.modalidad?.label || 'No especificada'}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>

                  <CardContent>
                    <Button
                      fullWidth
                      variant="contained"
                      color="primary"
                      onClick={() => navigate(`/vacante-publica/${vacancy.id}`)}
                    >
                      Ver Detalles
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Paginación */}
          {pagination.totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={4}>
              <Pagination
                count={pagination.totalPages}
                page={pagination.page}
                onChange={handlePageChange}
                color="primary"
                size="large"
              />
            </Box>
          )}
        </>
      )}

      {/* Footer informativo */}
      <Box mt={6} p={3} bgcolor="grey.100" borderRadius={2}>
        <Typography variant="h6" gutterBottom>
          ¿Cómo postular a una vacante?
        </Typography>
        <Typography variant="body2" paragraph>
          Para postular a cualquiera de estas vacantes, debes registrarte en la
          Plataforma VCC de la Agencia Distrital de Empleo y completar tu perfil profesional.
        </Typography>
        <Button variant="outlined" color="primary" onClick={() => window.open(window.location.origin, '_blank')}>
          Registrarme en la plataforma
        </Button>
      </Box>
    </Container>
  );
};

export default PublicVacancyPage;